import { api } from '../../api/client';

/**
 * encountersApi — client for /api/encounters.
 *
 * Lives here rather than in api/client.js for the same reason growthApi does:
 * the whole feature is contained in screens/people, and the shared client is
 * already long enough that a feature nobody outside this directory calls does
 * not belong in it.
 *
 * Note there is deliberately no bulk fetch or export method, matching the
 * backend — see the guardrail note at the top of routes/encounters.js. `list`
 * returns the caller's own encounters and nothing else.
 */
export const encountersApi = {
  // ── Reference data, served rather than hard-coded client-side ──────────────
  stages:   ()   => api.get('/api/encounters/stages'),
  settings: ()   => api.get('/api/encounters/settings'),

  // ── Reads ─────────────────────────────────────────────────────────────────
  list:   (params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.limit)  q.set('limit', String(params.limit));
    const qs = q.toString();
    return api.get(`/api/encounters${qs ? `?${qs}` : ''}`);
  },
  get:    (id)          => api.get(`/api/encounters/${id}`),
  person: (recipientId) => api.get(`/api/encounters/person/${recipientId}`),

  // ── Writes ────────────────────────────────────────────────────────────────
  saveProfile: (recipientId, profile) =>
    api.put(`/api/encounters/person/${recipientId}/profile`, { profile }),

  create: ({ recipientId, setting, goal, plannedFor, mode }) =>
    api.post('/api/encounters', { recipientId, setting, goal, plannedFor, mode }),

  // When (or where) a planned conversation is. Moving the date re-arms both
  // reminders server-side.
  update: (id, { plannedFor, setting, goal }) =>
    api.patch(`/api/encounters/${id}`, { plannedFor, setting, goal }),

  generateArc:   (id) => api.post(`/api/encounters/${id}/generate/arc`),
  generateBeats: (id) => api.post(`/api/encounters/${id}/generate/beats`),

  addNote: (id, text) => api.patch(`/api/encounters/${id}/notes`, { text }),

  debrief:      (id, answers) => api.post(`/api/encounters/${id}/debrief`, answers),
  applyDebrief: (id, accepted) => api.post(`/api/encounters/${id}/debrief/apply`, accepted),

  // The ONLY way a stage changes. Separate from applyDebrief by design — see
  // guardrail 1. The response reports crossedFaithBoundary so the UI can say so
  // in words.
  setStage: (recipientId, { stage, note, encounterId }) =>
    api.post(`/api/encounters/recipients/${recipientId}/stage`, { stage, note, encounterId }),

  remove: (id) => api.delete(`/api/encounters/${id}`),
};

/**
 * careItemsApi — stressings and blessings, per person (/api/care-items).
 *
 * Here rather than in client.js for the same containment reason as the above;
 * PrayerScreen reads them off the calendar payload and needs no client at all.
 */
export const careItemsApi = {
  list:   (recipientId)       => api.get(`/api/care-items/person/${recipientId}`),
  create: (recipientId, item) => api.post(`/api/care-items/person/${recipientId}`, item),
  update: (id, patch)         => api.patch(`/api/care-items/${id}`, patch),
  close:  (id, outcome)       => api.patch(`/api/care-items/${id}`, { status: 'closed', outcome: outcome || null }),
  reopen: (id)                => api.patch(`/api/care-items/${id}`, { status: 'open' }),
  remove: (id)                => api.delete(`/api/care-items/${id}`),
};

// ── Two-phase generation orchestrator ────────────────────────────────────────
// Mirrors runTwoPhaseGeneration in courses/growthApi.js, including its retry.
// The backend split /generate into two calls so neither one races a timeout; the
// client is what has to drive them and show that something is happening.

async function withRetry(fn, retries = 1, delayMs = 3000) {
  try { return await fn(); }
  catch (err) {
    // A 4xx will not become a 2xx on a retry — only retry what might be
    // transient. 502 is what the route returns when the model misbehaves, and
    // that genuinely can succeed second time.
    const status = err?.status || err?.response?.status;
    if (retries <= 0 || (status && status >= 400 && status < 500)) throw err;
    await new Promise(r => setTimeout(r, delayMs));
    return withRetry(fn, retries - 1, delayMs);
  }
}

/**
 * Generate a full guide for an existing encounter.
 *
 * `onStatus(message)` is called between phases. Phase 2 is where nearly all the
 * time goes — it is also where a stage violation can force a regeneration, so it
 * can legitimately take a while and the caller needs to say so rather than
 * showing a bare spinner.
 *
 * Returns the finished encounter. Throws with a usable message on failure; the
 * arc survives on the server either way, so a failed phase 2 can be retried
 * without redoing phase 1.
 */
export async function generateGuide(encounterId, onStatus = () => {}) {
  onStatus('shaping');
  await withRetry(() => encountersApi.generateArc(encounterId));

  onStatus('writing');
  const { encounter } = await withRetry(() => encountersApi.generateBeats(encounterId));

  onStatus('done');
  return encounter;
}

/**
 * Has this encounter got a guide that is actually usable?
 *
 * Two shapes reach this, and both have to work, because this one answer decides
 * where a row routes:
 *
 *   - list rows (`GET /api/encounters`) carry `hasGuide`, a boolean the server
 *     derives — the guide itself is far too large to send 50 of
 *   - full reads (`GET /:id`, `GET /person/:id`) carry the real `generatedGuide`
 *
 * Checking only `generatedGuide` is what broke the Encounters tab: list rows
 * never have it, so every planned conversation read as unplanned. Keep both
 * branches, and keep this the only place that decides.
 */
export function guideIsComplete(encounter) {
  if (typeof encounter?.hasGuide === 'boolean') return encounter.hasGuide;
  const g = encounter?.generatedGuide;
  return !!(g && Array.isArray(g.beats) && g.beats.length > 0);
}

/**
 * Has this conversation's time come and gone without a debrief?
 *
 * An hour's grace after the planned time, so a row does not flip to "how did it
 * go?" while you are still sitting there. A conversation with no date counts
 * once something was written during it (status in_progress).
 */
export const AWAITING_GRACE_MS = 60 * 60 * 1000;
export function isAwaitingDebrief(e, now = Date.now()) {
  if (!e || e.status === 'debriefed') return false;
  if (e.plannedFor) return new Date(e.plannedFor).getTime() + AWAITING_GRACE_MS <= now;
  return e.status === 'in_progress';
}

/**
 * Where tapping a conversation goes. The ONE routing rule, shared by the
 * Encounters tab and the person's History so the two cannot drift:
 *
 *   held                     → its debrief
 *   time has passed, not held → its debrief ("how did it go?")
 *   has a guide              → the guide
 *   otherwise                → the planning form, carrying which conversation
 *
 * The middle rule never regenerates anything — the debrief offers the guide
 * back — so it does not reopen the bug guideIsComplete() exists for.
 */
export function conversationRoute(e, recipientId = e?.recipientId, now = Date.now()) {
  if (e.status === 'debriefed')    return `/encounters/${e.id}/debrief`;
  if (isAwaitingDebrief(e, now))   return `/encounters/${e.id}/debrief`;
  if (guideIsComplete(e))          return `/encounters/${e.id}/guide`;
  return `/people/person/${recipientId}/plan?encounter=${e.id}`;
}

/**
 * The most recent conversation actually held, from a person's encounter list —
 * what "last time" means on the plan screen. (GET /:id computes the same thing
 * server-side for the live guide, which may not make a second request.)
 */
export function lastHeldFrom(encounters, excludeId) {
  const held = (encounters || [])
    .filter(e => e.status === 'debriefed' && e.id !== excludeId)
    .sort((a, b) => new Date(b.debriefedAt || 0) - new Date(a.debriefedAt || 0));
  const e = held[0];
  if (!e) return null;
  const d = (e.debrief && typeof e.debrief === 'object') ? e.debrief : {};
  return {
    id: e.id, debriefedAt: e.debriefedAt, setting: e.setting,
    learned: d.learned || null, godMoved: d.godMoved || null,
    nextStep: e.nextStep || d.nextStep || null,
  };
}
